import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

export const assetTypeFieldTypeDtoSchema = z.enum([
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "DATE",
]);

export const assetTypeFieldWriteDtoSchema = z.object({
  nameEn: required,
  nameAr: required,
  type: assetTypeFieldTypeDtoSchema,
  isRequired: z.boolean(),
  isUnique: z.boolean(),
});

export const assetTypeFieldDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  type: assetTypeFieldTypeDtoSchema,
  isRequired: z.boolean(),
  isUnique: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  createdAt: required,
  updatedAt: required,
  assetTypeId: required,
  createdById: required,
});

export const assetTypeStatusDtoSchema = z.enum(["ACTIVE", "INACTIVE"]);

export const assetTypeAccountDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
});

/** Shape shared by `GET /asset-types` rows and the `POST /asset-types` record. */
export const assetTypeDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  remarks: z.string().nullable(),
  attachmentsAllowed: z.boolean(),
  status: assetTypeStatusDtoSchema,
  createdAt: required,
  updatedAt: required,
  accountId: required,
  account: assetTypeAccountDtoSchema,
  // Documented as always present, but the live API sometimes omits both --
  // tolerated as null either way rather than failing the whole parse over a
  // field this domain doesn't otherwise use.
  createdById: required.nullable().optional(),
  updatedById: required.nullable().optional(),
  fields: z.array(assetTypeFieldDtoSchema),
  // Confirmed on GET /asset-types/:id; not confirmed on the list or
  // create/update responses, so left optional here rather than assumed
  // present everywhere this shared schema is reused.
  relatedAssetsCount: z.number().int().nonnegative().optional(),
});

export const assetTypesListResponseDtoSchema = z.object({
  data: z.array(assetTypeDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const assetTypeSummaryDtoSchema = z.object({
  totalAssetTypes: z.number().int().nonnegative(),
  totalActiveAssetTypes: z.number().int().nonnegative(),
  totalInactiveAssetTypes: z.number().int().nonnegative(),
});

export const assetTypeSummaryResponseDtoSchema = z.object({
  data: assetTypeSummaryDtoSchema,
});

export const assetTypeCreateDtoSchema = z.object({
  accountId: required.optional(),
  nameEn: required,
  nameAr: required,
  remarks: z.string().trim().optional(),
  attachmentsAllowed: z.boolean(),
  // No `.min(1)` -- GET /assets/form now returns nameEn/nameAr on every
  // asset type by default (see AssetTypeFormScreen), so a type with no
  // custom attributes of its own is legitimate: an empty array, not an
  // error. `.min(1)` here used to reject that case client-side with a raw,
  // unlocalized Zod dump before the request ever reached the backend.
  fields: z.array(assetTypeFieldWriteDtoSchema),
});

// `PUT /asset-types/:id` -- nameEn/nameAr/remarks/status move as a partial
// update, same as before. `fields`, when sent, is the whole desired form in
// exactly the shape `assetTypeCreateDtoSchema.fields` takes (id-less,
// matched against what's stored by label) -- no `.min(1)` for the same
// reason as create's own `fields`: dropping every custom field is legitimate.
export const assetTypeUpdateDtoSchema = z.object({
  nameEn: required.optional(),
  nameAr: required.optional(),
  remarks: z.string().trim().optional(),
  status: assetTypeStatusDtoSchema.optional(),
  fields: z.array(assetTypeFieldWriteDtoSchema).optional(),
});

/**
 * The `{ message, data }` envelope shared by every asset-type call that
 * returns the full record: create, update, restore, and getById (whose
 * response omits `message`, hence it staying optional here).
 */
export const assetTypeRecordResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: assetTypeDtoSchema,
});

/** `DELETE /asset-types/:id` -- a bare `{ message }` envelope, no record. */
export const assetTypeDeleteResponseDtoSchema = z.object({
  message: required,
});

// PROVISIONAL: `POST /asset-types` returns 201 for both a real creation and
// an archived-match (same two-outcome shape confirmed for accounts/admins),
// and `PATCH /asset-types/:id/restore`'s response is confirmed (the full
// record, above) -- but this trimmed archived-match payload itself hasn't
// been seen yet. Modeled defensively (only `id` required) until it is;
// tighten once confirmed.
export const archivedAssetTypeMatchDtoSchema = z.object({
  id: required,
  nameEn: required.optional(),
  nameAr: required.optional(),
});

export const archivedAssetTypeMatchResponseDtoSchema = z.object({
  data: archivedAssetTypeMatchDtoSchema,
  message: required,
});

// `GET /asset-types/archived` -- `fieldsCount` was confirmed sent as
// `assetsCount` (a backend bug) until the backend fixed it; now sent under
// its correct name, matching the active list's own column.
export const archivedAssetTypeListItemDtoSchema = z.object({
  id: required,
  nameEn: required,
  nameAr: required,
  status: assetTypeStatusDtoSchema,
  account: assetTypeAccountDtoSchema,
  fieldsCount: z.number().int().nonnegative(),
  archivedAt: required,
});

export const archivedAssetTypeListResponseDtoSchema = z.object({
  data: z.array(archivedAssetTypeListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export type AssetTypeFieldDto = z.infer<typeof assetTypeFieldDtoSchema>;
export type AssetTypeDto = z.infer<typeof assetTypeDtoSchema>;
export type AssetTypeSummaryDto = z.infer<typeof assetTypeSummaryDtoSchema>;
export type AssetTypeCreateDto = z.infer<typeof assetTypeCreateDtoSchema>;
export type AssetTypeUpdateDto = z.infer<typeof assetTypeUpdateDtoSchema>;
export type ArchivedAssetTypeMatchDto = z.infer<
  typeof archivedAssetTypeMatchDtoSchema
>;
export type ArchivedAssetTypeListItemDto = z.infer<
  typeof archivedAssetTypeListItemDtoSchema
>;
